import { toast } from 'sonner';
import { useMemo, useEffect, useContext, useReducer, useCallback, createContext } from 'react';

import { createAuthenticatedAxiosInstance } from 'src/utils/axios';

import { zerreactorBackendEndpoints } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendEndpoints';

import { ACCESS_TOKEN_KEY } from 'src/auth/context/jwt/constant';

// Storage keys for virtual login session
const VIRTUAL_SESSION_KEY = 'virtual_session';
const ADMIN_BACKUP_KEY = 'admin_backup';

// Initial state
const initialState = {
  isVirtualLogin: false,
  originalAdmin: null,
  virtualUser: null,
  virtualTokens: null,
  sessionStartTime: null,
  loading: false,
};

// Action types
const VIRTUAL_LOGIN_ACTIONS = {
  START_LOADING: 'START_LOADING',
  STOP_LOADING: 'STOP_LOADING',
  START_VIRTUAL_LOGIN: 'START_VIRTUAL_LOGIN',
  EXIT_VIRTUAL_LOGIN: 'EXIT_VIRTUAL_LOGIN',
  RESTORE_SESSION: 'RESTORE_SESSION',
  UPDATE_TIMER: 'UPDATE_TIMER',
};

// Reducer
function virtualLoginReducer(state, action) {
  switch (action.type) {
    case VIRTUAL_LOGIN_ACTIONS.START_LOADING:
      return { ...state, loading: true };

    case VIRTUAL_LOGIN_ACTIONS.STOP_LOADING:
      return { ...state, loading: false };

    case VIRTUAL_LOGIN_ACTIONS.START_VIRTUAL_LOGIN:
      return {
        ...state,
        isVirtualLogin: true,
        originalAdmin: action.payload.originalAdmin,
        virtualUser: action.payload.virtualUser,
        virtualTokens: action.payload.virtualTokens,
        sessionStartTime: action.payload.sessionStartTime,
        loading: false,
      };

    case VIRTUAL_LOGIN_ACTIONS.EXIT_VIRTUAL_LOGIN:
      return {
        ...state,
        isVirtualLogin: false,
        originalAdmin: null,
        virtualUser: null,
        virtualTokens: null,
        sessionStartTime: null,
        loading: false,
      };

    case VIRTUAL_LOGIN_ACTIONS.RESTORE_SESSION:
      return {
        ...state,
        ...action.payload,
        loading: false,
      };

    default:
      return state;
  }
}

// Context with default value
const defaultContextValue = {
  isVirtualLogin: false,
  originalAdmin: null,
  virtualUser: null,
  virtualTokens: null,
  sessionStartTime: null,
  loading: false,
  startVirtualLogin: () => Promise.reject(new Error('VirtualLoginProvider not initialized')),
  exitVirtualLogin: () => {},
  clearVirtualSession: () => {},
  getCurrentTokens: () => ({ accessToken: null, refreshToken: null }),
  updateVirtualUserData: () => {},
};

const VirtualLoginContext = createContext(defaultContextValue);

// Provider component
export function VirtualLoginProvider({ children }) {
  const [state, dispatch] = useReducer(virtualLoginReducer, initialState);

  // Start virtual login session
  const startVirtualLogin = useCallback(async (userId, currentUser) => {
    dispatch({ type: VIRTUAL_LOGIN_ACTIONS.START_LOADING });

    try {
      // Create authenticated axios instance with current user's token
      const currentAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const authenticatedAxios = createAuthenticatedAxiosInstance(currentAccessToken);

      // Step 1: BACKUP all current localStorage data (admin session)
      // This ensures we save everything before clearing
      const currentUserData = localStorage.getItem('userData');
      const currentUserDataObj = currentUserData ? JSON.parse(currentUserData) : currentUser;

      // Backup ALL localStorage data
      const allLocalStorageData = {};
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key) {
          allLocalStorageData[key] = localStorage.getItem(key);
        }
      }

      const adminBackupData = {
        user: currentUserDataObj,
        tokens: {
          accessToken: localStorage.getItem('jwt_access_token'),
          refreshToken: localStorage.getItem('jwt_refresh_token'),
        },
        allData: allLocalStorageData, // Backup everything
      };

      // Step 2: Call virtual login API to get tokens BEFORE clearing localStorage
      const response = await authenticatedAxios.post(
        `${zerreactorBackendEndpoints.Auth.VirtualLogin}?UserId=${userId}`
      );

      // Check if virtual login response has the expected format
      // API returns: { AccessToken: "...", RefreshToken: "..." }
      if (!response.data || !response.data.AccessToken || !response.data.RefreshToken) {
        throw new Error('Invalid virtual login response format from server');
      }

      const { AccessToken, RefreshToken } = response.data;

      // Step 3: CLEAR all localStorage (fresh start for virtual user)
      localStorage.clear();

      // Step 4: Save admin backup AFTER clearing
      localStorage.setItem(ADMIN_BACKUP_KEY, JSON.stringify(adminBackupData));

      // Step 5: Set virtual user tokens to current session
      localStorage.setItem('jwt_access_token', AccessToken);
      localStorage.setItem('jwt_refresh_token', RefreshToken);

      // Step 6: Get virtual user profile data using the new token
      let virtualUser;
      try {
        // Create new axios instance with virtual user's token
        const virtualUserAxios = createAuthenticatedAxiosInstance(AccessToken);
        const profileRes = await virtualUserAxios.get(zerreactorBackendEndpoints.Auth.profile);

        // Check if profile response has user data
        if (profileRes.data && profileRes.data.Id && profileRes.data.FirstName) {
          virtualUser = profileRes.data;

          // Save virtual user data to localStorage (fresh start)
          localStorage.setItem('userData', JSON.stringify(virtualUser));
        } else {
          throw new Error('Invalid profile response format from server');
        }
      } catch (profileError) {
        console.error('Error fetching virtual user profile:', profileError);
        // If profile fetch fails, restore admin session and abort
        localStorage.clear();
        const backup = JSON.parse(JSON.stringify(adminBackupData.allData));
        Object.keys(backup).forEach((key) => {
          localStorage.setItem(key, backup[key]);
        });
        throw new Error('Failed to fetch virtual user profile data. Please try again.');
      }

      const sessionData = {
        originalAdmin: currentUser,
        virtualUser,
        virtualTokens: {
          accessToken: AccessToken,
          refreshToken: RefreshToken,
        },
        sessionStartTime: Date.now(),
      };

      // Save virtual session data
      localStorage.setItem(VIRTUAL_SESSION_KEY, JSON.stringify(sessionData));

      dispatch({
        type: VIRTUAL_LOGIN_ACTIONS.START_VIRTUAL_LOGIN,
        payload: sessionData,
      });

      setTimeout(() => {
        window.location.href = '/';
      }, 500);

      toast.success(`Virtual login started for ${virtualUser.FirstName} ${virtualUser.LastName}`);

      return sessionData;
    } catch (error) {
      dispatch({ type: VIRTUAL_LOGIN_ACTIONS.STOP_LOADING });
      console.error('Virtual login failed:', error);

      // Extract meaningful error message from backend response
      let errorMessage = 'Virtual login failed';

      try {
        // Try multiple ways to get the error message
        const responseData = error?.response?.data;

        // Check if error object itself contains the response (not in error.response.data)
        const errorData = responseData || error;

        if (errorData) {
          // Priority: ExceptionMessage > Message (if not generic) > fallback
          if (errorData.ExceptionMessage && errorData.ExceptionMessage.trim()) {
            errorMessage = errorData.ExceptionMessage;
          } else if (
            errorData.Message &&
            errorData.Message.trim() &&
            errorData.Message !== 'An error occurred during the request.'
          ) {
            errorMessage = errorData.Message;
          }
          // Also try common error field variations
          else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.errorMessage) {
            // eslint-disable-next-line prefer-destructuring
            errorMessage = errorData.errorMessage;
          } else if (error?.message) {
            // Network or other errors
            errorMessage = error.message;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
        // Keep default message if parsing fails
      }

      toast.error(errorMessage);
      throw error;
    }
  }, []);

  // Exit virtual login session
  const exitVirtualLogin = useCallback(() => {
    try {
      // Get admin backup data
      const adminBackup = localStorage.getItem(ADMIN_BACKUP_KEY);

      if (!adminBackup) {
        toast.error('No admin backup found. Cannot exit virtual login.');
        return;
      }

      const adminBackupData = JSON.parse(adminBackup);

      // Step 1: CLEAR all localStorage (remove virtual user data completely)
      localStorage.clear();

      // Step 2: RESTORE all admin localStorage data
      if (adminBackupData.allData) {
        // Restore everything except the backup keys
        Object.keys(adminBackupData.allData).forEach((key) => {
          if (key !== ADMIN_BACKUP_KEY && key !== VIRTUAL_SESSION_KEY) {
            localStorage.setItem(key, adminBackupData.allData[key]);
          }
        });
      } else {
        // Fallback: restore only tokens and user data (for backward compatibility)
        if (adminBackupData.tokens) {
          localStorage.setItem('jwt_access_token', adminBackupData.tokens.accessToken);
          localStorage.setItem('jwt_refresh_token', adminBackupData.tokens.refreshToken);
        }

        if (adminBackupData.user) {
          localStorage.setItem('userData', JSON.stringify(adminBackupData.user));
        }
      }

      dispatch({ type: VIRTUAL_LOGIN_ACTIONS.EXIT_VIRTUAL_LOGIN });

      toast.success('Exited virtual login session. Restored admin session.');

      // Reload page to reinitialize everything with admin session
      window.location.href = '/user';
    } catch (error) {
      console.error('Error exiting virtual login:', error);
      toast.error('Error exiting virtual login. Please try logging in again.');
    }
  }, []);

  // Clear virtual session (called when signout happens)
  const clearVirtualSession = useCallback(() => {
    if (state.isVirtualLogin) {
      dispatch({ type: VIRTUAL_LOGIN_ACTIONS.EXIT_VIRTUAL_LOGIN });
    }
  }, [state.isVirtualLogin]);

  // Check and restore session from localStorage
  const checkAndRestoreSession = useCallback(() => {
    const storedSession = localStorage.getItem(VIRTUAL_SESSION_KEY);
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);

        // Check if session is still valid (max 8 hours)
        const maxSessionTime = 8 * 60 * 60 * 1000; // 8 hours
        const isSessionValid = Date.now() - sessionData.sessionStartTime < maxSessionTime;

        if (isSessionValid) {
          dispatch({
            type: VIRTUAL_LOGIN_ACTIONS.RESTORE_SESSION,
            payload: {
              isVirtualLogin: true,
              ...sessionData,
            },
          });
        } else {
          // Session expired, clean up
          localStorage.removeItem(VIRTUAL_SESSION_KEY);
          localStorage.removeItem(ADMIN_BACKUP_KEY);
        }
      } catch (error) {
        console.error('Error restoring virtual session:', error);
        localStorage.removeItem(VIRTUAL_SESSION_KEY);
        localStorage.removeItem(ADMIN_BACKUP_KEY);
      }
    } else if (state.isVirtualLogin) {
      // No virtual session found, clear state if it exists
      dispatch({ type: VIRTUAL_LOGIN_ACTIONS.EXIT_VIRTUAL_LOGIN });
    }
  }, [state.isVirtualLogin]);

  // Restore session on page load
  useEffect(() => {
    checkAndRestoreSession();
  }, [checkAndRestoreSession]);

  // Listen for localStorage changes (signout from other tabs or components)
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Check if virtual session keys were modified
      if (event.key === VIRTUAL_SESSION_KEY || event.key === ADMIN_BACKUP_KEY) {
        checkAndRestoreSession();
      }
    };

    const handleSignOut = () => {
      clearVirtualSession();
    };

    // Listen for localStorage changes
    window.addEventListener('storage', handleStorageChange);
    // Listen for custom signout event
    window.addEventListener('virtualLoginSignOut', handleSignOut);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('virtualLoginSignOut', handleSignOut);
    };
  }, [checkAndRestoreSession, clearVirtualSession]);

  // Get current active tokens (virtual or original)
  const getCurrentTokens = useCallback(() => {
    if (state.isVirtualLogin && state.virtualTokens) {
      return state.virtualTokens;
    }

    return {
      accessToken: localStorage.getItem('jwt_access_token'),
      refreshToken: localStorage.getItem('jwt_refresh_token'),
    };
  }, [state.isVirtualLogin, state.virtualTokens]);

  // Update virtual user data (useful for external updates like competition changes)
  const updateVirtualUserData = useCallback(
    (updatedUserData) => {
      if (!state.isVirtualLogin) {
        console.warn('updateVirtualUserData called but not in virtual login mode');
        return;
      }

      // Update virtual user in state
      const updatedVirtualUser = { ...state.virtualUser, ...updatedUserData };

      // Update session data in localStorage
      const storedSession = localStorage.getItem(VIRTUAL_SESSION_KEY);
      if (storedSession) {
        try {
          const sessionData = JSON.parse(storedSession);
          sessionData.virtualUser = updatedVirtualUser;
          localStorage.setItem(VIRTUAL_SESSION_KEY, JSON.stringify(sessionData));
        } catch (error) {
          console.error('Error updating virtual session data:', error);
        }
      }

      // Update state
      dispatch({
        type: VIRTUAL_LOGIN_ACTIONS.START_VIRTUAL_LOGIN,
        payload: {
          originalAdmin: state.originalAdmin,
          virtualUser: updatedVirtualUser,
          virtualTokens: state.virtualTokens,
          sessionStartTime: state.sessionStartTime,
        },
      });
    },
    [state]
  );

  const value = useMemo(() => {
    const contextValue = {
      ...state,
      startVirtualLogin,
      exitVirtualLogin,
      clearVirtualSession,
      getCurrentTokens,
      updateVirtualUserData,
    };

    return contextValue;
  }, [
    state,
    startVirtualLogin,
    exitVirtualLogin,
    clearVirtualSession,
    getCurrentTokens,
    updateVirtualUserData,
  ]);

  return <VirtualLoginContext.Provider value={value}>{children}</VirtualLoginContext.Provider>;
}

// Hook to use virtual login context
export function useVirtualLogin() {
  const context = useContext(VirtualLoginContext);

  // Always return a valid context, even if undefined
  if (context === undefined || context === null) {
    console.warn(
      'useVirtualLogin context is undefined. Using default values. This might indicate a provider setup issue.'
    );
    return defaultContextValue;
  }

  return context;
}
